import React from "react";
import { useToast } from "@/hooks/use-toast";
import { Order } from "@/types/orders";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Clock,
  Package,
  CreditCard,
  ChevronDown,
  ChevronUp,
  Receipt,
  X,
} from "lucide-react";
import { createOrderReminder, requestPayment } from "@/services/tables";
import { useTranslation } from "react-i18next";
import { formatCurrency } from "@/lib/currencyFormatter";
import { useFeatureFlagStore } from "@/store/featureFlagStore";

// Import our custom hooks
import { useOrderReminders } from "./order-view/useOrderReminders";
import { useOrderTime } from "./order-view/useOrderTime";
import { CustomerCancelOrderDialog } from "./CustomerCancelOrderDialog";

interface CurrentOrderViewProps {
  order: Order;
  storeId: string;
  tableId: string;
  storeName?: string;
}

export function CurrentOrderView({
  order,
  storeId,
  tableId,
  storeName,
}: CurrentOrderViewProps) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [expandedItems, setExpandedItems] = React.useState(false);
  const [showCancelDialog, setShowCancelDialog] = React.useState(false);

  // Use our custom hooks
  const {
    reminderSent,
    reminderDisabled,
    reminderCountdown,
    setReminderStatus,
  } = useOrderReminders(order.id);
  const { orderTimePassed } = useOrderTime(order.createdAt);

  // Debug order data - enhanced debugging
  React.useEffect(() => {
    if (!order || typeof order !== "object") {
      console.error("Invalid order object:", order);
    }

    if (order && !order.createdAt) {
      console.error("Order missing createdAt:", order);
    }

    if (
      order &&
      order.createdAt &&
      isNaN(new Date(order.createdAt).getTime())
    ) {
      console.error("Invalid createdAt value:", order.createdAt);
    }

    // Check if items array exists and is valid
    if (!Array.isArray(order.items)) {
      console.error("Order items is not an array:", order.items);
    } else if (order.items.length === 0) {
      console.warn("Order has no items:", order.id);
    }
  }, [order]);

  // Group items by menu item for condensed display
  const groupedItems = React.useMemo(() => {
    if (!Array.isArray(order.items)) return [];

    const grouped = order.items.reduce((acc, item) => {
      const existingItem = acc.find(
        (i) => i.menu_item_id === item.menu_item_id
      );
      if (existingItem) {
        existingItem.quantity += item.quantity;
      } else {
        acc.push({ ...item });
      }
      return acc;
    }, [] as typeof order.items);

    return grouped;
  }, [order.items]);

  const totalItems = React.useMemo(() => {
    if (!Array.isArray(order.items)) return 0;
    return order.items.reduce((sum, item) => sum + item.quantity, 0);
  }, [order.items]);

  const handleSendReminder = async () => {
    try {
      const result = await createOrderReminder(
        order.id,
        tableId,
        storeId,
        "Customer reminder: Order is taking too long"
      );

      if (result.success) {
        setReminderStatus(true);

        toast({
          title: t("reminderSent"),
          description: t("staffWillBeNotifiedAboutYourOrder"),
          duration: 3000,
        });
      } else {
        throw new Error(result.error);
      }
    } catch (error) {
      console.error("Error sending reminder:", error);
      toast({
        title: t("error"),
        description: t("failedToSendReminder"),
        variant: "destructive",
        duration: 3000,
      });
    }
  };

  const handleRequestPayment = async () => {
    try {
      // Check if payment feature is enabled
      const isPaymentEnabled = useFeatureFlagStore
        .getState()
        .checkFlag("activate_payment_request");

      // Create payment request record for manual payment
      const requestResult = await requestPayment(order.id, tableId, storeId);
      if (!requestResult.success) {
        throw new Error(requestResult.error);
      }

      // Show success message for manual payment
      toast({
        title: t("paymentRequestSent"),
        description: t("staffWillComeToProcessYourPayment"),
        duration: 3000,
      });
    } catch (error) {
      console.error("Error requesting payment:", error);
      toast({
        title: t("error"),
        description: t("failedToRequestPayment"),
        variant: "destructive",
        duration: 3000,
      });
    }
  };

  // Get appropriate status colors
  const getStatusColorClasses = () => {
    switch (order.status) {
      case "pending":
        return "bg-amber-50 border-amber-200 text-amber-700";
      case "preparing":
        return "bg-blue-50 border-blue-200 text-blue-700";
      case "ready":
        return "bg-green-50 border-green-200 text-green-700";
      case "delivered":
        return "bg-purple-50 border-purple-200 text-purple-700";
      case "completed":
        return "bg-gray-50 border-gray-200 text-gray-700";
      case "cancelled":
        return "bg-red-50 border-red-200 text-red-700";
      default:
        return "bg-gray-50 border-gray-200 text-gray-700";
    }
  };

  const getStatusIcon = () => {
    switch (order.status) {
      case "pending":
        return <Clock className="h-5 w-5 text-amber-500" />;
      case "preparing":
        return <Clock className="h-5 w-5 text-blue-500 animate-spin-slow" />;
      case "ready":
        return <Package className="h-5 w-5 text-green-500" />;
      default:
        return <Clock className="h-5 w-5 text-gray-500" />;
    }
  };

  const getStatusText = () => {
    switch (order.status) {
      case "pending":
        return t("waitingForKitchen");
      case "preparing":
        return t("preparingYourOrder");
      case "ready":
        return t("orderIsReady");
      case "delivered":
        return t("orderDelivered");
      case "completed":
        return t("orderCompleted");
      case "cancelled":
        return t("orderCancelled");
      default:
        return t("processing");
    }
  };
  return (
    <Card
      className={`border rounded-lg overflow-hidden shadow-sm transition-colors ${getStatusColorClasses()}`}
    >
      <CardHeader className="p-4 pb-2 bg-white/60 backdrop-blur-sm">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            {getStatusIcon()}
            <div>
              <h3 className="font-medium text-lg">{getStatusText()}</h3>
              <p className="text-xs text-muted-foreground">
                {t("orderedAt")}{" "}
                {orderTimePassed === "justNow" ? t("justNow") : orderTimePassed}{" "}
                ·<span className="ml-1">#{order.id.slice(0, 5)}</span>
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {order.is_payed && (
              <Badge
                variant="outline"
                className="bg-green-100 text-green-800 border-green-200"
              >
                $ {t("paid")}
              </Badge>
            )}
            <Badge
              variant="outline"
              className={`px-3 py-1 ${
                order.status === "pending"
                  ? "bg-amber-100 text-amber-800 border-amber-200"
                  : order.status === "preparing"
                  ? "bg-blue-100 text-blue-800 border-blue-200"
                  : order.status === "ready"
                  ? "bg-green-100 text-green-800 border-green-200"
                  : "bg-gray-100 text-gray-800 border-gray-200"
              }`}
            >
              {t(order.status)}
            </Badge>
          </div>
        </div>
      </CardHeader>

      <CardContent className="p-4 pt-2">
        {/* Order Summary */}
        <div className="flex items-center justify-between py-3">
          <div className="flex items-center gap-2">
            <Badge variant="secondary" className="rounded-full px-2.5">
              {totalItems} {totalItems === 1 ? t("item") : t("items")}
            </Badge>
            <span className="text-sm text-muted-foreground">
              {order.tableName ? `${order.tableName}` : ""}
              {order.tableNumber
                ? `${order.tableName ? " · " : ""}${t("table")} ${
                    order.tableNumber
                  }`
                : ""}
            </span>
          </div>
          <div className="font-semibold">
            {formatCurrency(order.totalAmount)}
          </div>
        </div>

        {/* Order Items - Collapsed View */}
        <div className="my-2 bg-white rounded-md border">
          <div
            className="p-3 flex items-center justify-between cursor-pointer hover:bg-gray-50 transition-colors rounded-md"
            onClick={() => setExpandedItems(!expandedItems)}
          >
            <div className="flex items-center gap-2">
              <Receipt className="h-4 w-4 text-muted-foreground" />
              <span className="font-medium">{t("orderDetails")}</span>
            </div>
            {expandedItems ? (
              <ChevronUp className="h-4 w-4 text-muted-foreground" />
            ) : (
              <ChevronDown className="h-4 w-4 text-muted-foreground" />
            )}
          </div>

          {expandedItems && (
            <div className="px-3 pb-3 space-y-2 animate-in slide-in-from-top-2 duration-200">
              {groupedItems.map((item) => (
                <div
                  key={item.id}
                  className="flex items-center justify-between py-2 border-t"
                >
                  <div className="flex items-center gap-3">
                    {item.imageUrl && (
                      <div className="h-12 w-12 rounded overflow-hidden bg-gray-100">
                        <img
                          src={item.imageUrl}
                          alt={item.menu_item_name}
                          className="h-full w-full object-cover"
                        />
                      </div>
                    )}
                    <div>
                      <p className="font-medium">{item.menu_item_name}</p>
                      <p className="text-xs text-muted-foreground">
                        {item.quantity > 1 && (
                          <span className="font-semibold">
                            {item.quantity}x
                          </span>
                        )}{" "}
                        {formatCurrency(item.price)}
                      </p>
                    </div>
                  </div>
                  <div className="font-medium">
                    {formatCurrency(item.price * item.quantity)}
                  </div>
                </div>
              ))}

              {order.specialInstructions && (
                <div className="mt-2 pt-2 border-t">
                  <p className="text-xs font-medium text-muted-foreground mb-1">
                    {t("specialInstructions")}:
                  </p>
                  <p className="text-sm p-2 bg-gray-50 rounded-md">
                    {order.specialInstructions}
                  </p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="mt-4 flex flex-row gap-3">
          {(order.status === "pending" || order.status === "preparing") && (
            <Button
              variant="outline"
              className="flex-1 border-dashed"
              onClick={handleSendReminder}
              disabled={reminderDisabled}
            >
              <Clock className="mr-2 h-4 w-4" />
              {reminderSent
                ? `${t("reminderSent")} (${reminderCountdown}s)`
                : t("sendReminder")}
            </Button>
          )}

          {(order.status === "ready" || order.status === "delivered") &&
            !order.is_payed && (
              <Button
                className="flex-1 bg-green-600 hover:bg-green-700 text-white"
                onClick={handleRequestPayment}
              >
                <CreditCard className="mr-2 h-4 w-4" />
                {t("requestPayment")}
              </Button>
            )}

          {/* Cancel Order Button */}
          {order.status !== "cancelled" && order.status !== "completed" && order.status !== "delivered" && (
            <Button
              variant="destructive"
              className="flex-1"
              onClick={() => setShowCancelDialog(true)}
            >
              <X className="mr-2 h-4 w-4" />
              {t("cancelOrder")}
            </Button>
          )}
        </div>
      </CardContent>

      {/* Customer Cancel Order Dialog */}
      <CustomerCancelOrderDialog
        open={showCancelDialog}
        onOpenChange={setShowCancelDialog}
        orderId={order.id}
        orderCreatedAt={order.createdAt}
        storeName={storeName || "this store"}
        onOrderCancelled={() => {
          // Refresh the page or trigger a refresh of orders
          window.location.reload();
        }}
      />
    </Card>
  );
}
